/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import ModalBodyLayout from '../shared/ModalBodyLayout';
import { showAlert } from '../../utilities/notifications';
import { cardProviders, manageCardScreens } from './constants';
import { useUserCardProvider } from '../../utilities/sharedHooks/user';
import { CARD_MONAVATE_PIN, USA_CARD_GET_PIN } from './queries';
import { encryptWithPublicKey } from '../../utilities/helpers';
import { useSetting } from '../../utilities/sharedHooks/settings';
import { PASSWORD_PUBLIC_KEY } from '../../utilities/sharedQueries';

const PinSettings = (props) => {
  const { t: translate } = useTranslation();
  const [cardCode, setCardCode] = useState('');
  const [encryptedPassword, setEncryptedPassword] = useState('');
  const { cardProvider } = useUserCardProvider();

  const [enablePasswordKMSEncryption] = useSetting(
    'app_features',
    'feature_toggle_enable_password_kms_encryption',
    false,
    false
  );

  const { data: publicKeyData } = useQuery(PASSWORD_PUBLIC_KEY, {
    enabled: !!enablePasswordKMSEncryption,
  });

  useEffect(() => {
    if (enablePasswordKMSEncryption && publicKeyData && cardCode) {
      const encryptPassword = async () => {
        try {
          const preparedPassword = await encryptWithPublicKey(publicKeyData?.publicKey, cardCode);
          setEncryptedPassword(preparedPassword);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      };
      encryptPassword();
    }
  }, [enablePasswordKMSEncryption, publicKeyData, cardCode]);

  const { refetch: getMastercardMonavatePin, isLoading: isLoadingViewMasterMonavatePin } = useQuery(
    [
      CARD_MONAVATE_PIN,
      { password: enablePasswordKMSEncryption ? encryptedPassword : encodeURI(cardCode) },
    ],
    {
      enabled: false,
      onSettled(data, error) {
        if (!error) {
          props.setPin(data.pin);
          props.openScreen(manageCardScreens.VIEW_PIN);
        }
        if (error) {
          // http status 200 response with error wrong password case
          if (!error?.request?.status) {
            showAlert({ message: error.message, type: 'danger' });
          }
        }
      },
      onError: (error) => {
        // http status 400 error response no data PIN not exist yet case
        if (error?.request?.status === 400) {
          props.setPin('');
          props.openScreen(manageCardScreens.VIEW_PIN);
        }
      },
    }
  );

  const { refetch: getGalileoPin, isLoading: isLoadingGalileoPIN } = useQuery(
    [
      USA_CARD_GET_PIN,
      { password: enablePasswordKMSEncryption ? encryptedPassword : encodeURI(cardCode) },
    ],
    {
      enabled: false,
      onSettled(data, error) {
        if (!error) {
          props.setPin(data?.url);
          props.openScreen(manageCardScreens.VIEW_PIN);
        }
        if (error) {
          // http status 200 response with error wrong password case
          if (!error?.request?.status) {
            showAlert({ message: error.message, type: 'danger' });
          }
        }
      },
      onError: (error) => {
        // http status 400 error response no data PIN not exist yet case
        if (error?.request?.status === 400) {
          props.setPin('');
          props.openScreen(manageCardScreens.VIEW_PIN);
        }
      },
    }
  );

  const handleChange = (e) => {
    if (!e) return;
    setCardCode(e?.target?.value || '');
  };

  const clearError = (e) => {
    if (e && e.target && e.target.className) {
      e.target.className = e?.target?.className.replace('has-error', '');
    }
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    if (cardProvider === cardProviders.MONAVATE) {
      getMastercardMonavatePin();
    } else if (cardProvider === cardProviders.GALILEO) {
      getGalileoPin();
    }
    return true;
  };

  const revealPassword = (e) => {
    const inputGroup = e.currentTarget.parentNode;
    const input = inputGroup.childNodes[0];
    if (input.type === 'password') {
      input.type = 'text';
      e.target.className = e.target.className.replace('open', 'close');
    } else if (input.type === 'text') {
      input.type = 'password';
      e.target.className = e.target.className.replace('close', 'open');
      e.target.style.top = '';
    }
  };

  const disabledBtn = useMemo(
    () => isLoadingViewMasterMonavatePin,
    [isLoadingViewMasterMonavatePin]
  );

  return (
    <ModalBodyLayout
      title={translate('manage_card_setting_option_pin_setting_title')}
      onPrevClick={() => props.openScreen(manageCardScreens.MAIN)}
      withBack
      isButtonInFooter>
      <div className="mt-4">
        <p className="text-f5 text-dark p-0">{translate('pin_setting_mastercard_subtitle')}</p>
      </div>
      <form onSubmit={handleSubmitForm}>
        <div className="form-group m-0 mt-3">
          <input
            type="password"
            className="form-control-input border-bottom-1"
            placeholder={translate('enter_password_placeholder')}
            name="cardCode"
            value={cardCode}
            onChange={handleChange}
            onFocus={clearError}
            required
          />
          <label className="floating-label">{translate('enter_password_placeholder')}</label>
          <label className="error-label">{translate('password_input_error_label')}</label>
          <span
            className="icon icon-open-icon h4"
            role="button"
            onClick={revealPassword}
            onKeyDown={() => {}}
            tabIndex={-1}
            aria-label={translate('label_reveal_pin')}
          />
        </div>
        <div className="d-flex justify-content-center bottom-0 position-relative mt-5 w-100">
          <button
            type="submit"
            disabled={disabledBtn}
            loading={isLoadingViewMasterMonavatePin || isLoadingGalileoPIN ? 'true' : 'false'}
            className="btn py-3 w-100 btn-primary">
            {translate('confirm_button_label')}
          </button>
        </div>
      </form>
    </ModalBodyLayout>
  );
};

export default PinSettings;

PinSettings.propTypes = {
  openScreen: PropTypes.func.isRequired,
  setPin: PropTypes.func.isRequired,
};
